import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { apiClient, fetchData } from "../../api/client";
import GenericTable from "../../components/GenericTable";
import {
  can,
  scopeSetFromPermissions,
  useEffectivePermissions,
} from "../../hooks/useEffectivePermissions";
import { useAuthContext } from "../../context/AuthContext";
import OmnichannelCampaignCreateDialog from "./components/OmnichannelCampaignCreateDialog";
import {
  formatCount,
  formatScheduleAt,
  formatOutboundChannelLabel,
  labelStatus,
  statusColor,
  toFriendlyMessage,
} from "./campaignUi";

const BRL = (n) =>
  Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function readBudget(campaign) {
  if (campaign?.budget != null && Number.isFinite(Number(campaign.budget))) {
    return Number(campaign.budget);
  }
  const cfg = campaign?.config_json;
  if (cfg?.budget != null && Number.isFinite(Number(cfg.budget))) return Number(cfg.budget);
  return 0;
}

function BroadcastBudgetEditDialog({ open, campaign, onClose, onSaved }) {
  const [budget, setBudget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !campaign) return;
    const current = readBudget(campaign);
    setBudget(current > 0 ? String(current) : "");
    setError("");
    setBusy(false);
  }, [open, campaign]);

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const value = budget === "" ? 0 : Number(budget);
      if (!Number.isFinite(value) || value < 0) {
        throw new Error("Informe um orçamento válido (R$ ≥ 0).");
      }
      await fetchData(`/campaigns/broadcasts/${campaign.id}`, "PATCH", { budget: value });
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(toFriendlyMessage(e) || e?.message || "Não foi possível salvar o orçamento.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>Editar orçamento</DialogTitle>
      <DialogContent dividers>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.55 }}>
          Disparo: <strong>{campaign?.name || `#${campaign?.id}`}</strong>
        </Typography>
        <TextField
          fullWidth
          type="number"
          label="Orçamento do disparo (R$)"
          value={budget}
          onChange={(e) => setBudget(e.target.value)}
          inputProps={{ min: 0, step: "0.01" }}
          helperText="Opcional. Usado no Dashboard → Campanhas para calcular custo por conversa e por venda."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={save} disabled={busy}>
          {busy ? "Salvando…" : "Salvar orçamento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Lista de disparos omnichannel.
 * @param {{ embedded?: boolean }} props — quando embedded, omite o título da página (usado no hub com abas).
 */
export default function WhatsappCampaignsListPage({ embedded = false } = {}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { userData } = useAuthContext();
  const { data: permissionsData } = useEffectivePermissions();
  const scopes = useMemo(
    () => scopeSetFromPermissions(permissionsData, userData),
    [permissionsData, userData]
  );
  const canCreate = can(scopes, "campaigns", "create");
  const canSend = can(scopes, "campaigns", "send");
  const canExport = can(scopes, "campaigns", "export");
  const canDelete = can(scopes, "campaigns", "delete");
  const canUpdate = can(scopes, "campaigns", "update");

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [budgetEditTarget, setBudgetEditTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const listRes = await fetchData("/campaigns/broadcasts", "GET");
      setCampaigns(listRes?.data || []);
    } catch (err) {
      setErrorMsg(toFriendlyMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return campaigns;
    return campaigns.filter((c) => {
      const hay = [
        c.name,
        c.tag,
        c.status,
        c.originName || c.origin_name,
        c.originIdentity || c.origin_identity,
        ...(c.channels || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });
  }, [campaigns, search]);

  const draftCount = useMemo(
    () => campaigns.filter((c) => c.status === "draft").length,
    [campaigns]
  );
  const runningCount = useMemo(
    () => campaigns.filter((c) => ["running", "scheduled"].includes(c.status)).length,
    [campaigns]
  );

  const rows = useMemo(
    () =>
      filtered.map((c) => ({
        id: c.id,
        name: c.name || "Disparo sem nome",
        status: c.status,
        channels: c.channels || [],
        originName: c.originName || c.origin_name || null,
        originIdentity: c.originIdentity || c.origin_identity || null,
        tag: c.tag || "",
        budget: readBudget(c),
        recipients: Number(c.recipients || 0),
        pending: Number(c.pending || 0),
        sent: Number(c.sent || 0),
        failed: Number(c.failed || 0),
        suppressed: Number(c.suppressed || 0),
        schedule_at: c.schedule_at,
        raw: c,
      })),
    [filtered]
  );

  const launch = async (id) => {
    setBusyId(id);
    setErrorMsg("");
    try {
      await fetchData(`/campaigns/broadcasts/${id}/launch`, "POST", {});
      await refresh();
    } catch (err) {
      setErrorMsg(toFriendlyMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = async (id) => {
    setBusyId(id);
    setErrorMsg("");
    try {
      const response = await apiClient.get(`/campaigns/broadcasts/${id}/export.csv`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `disparo-${id}-destinatarios.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMsg(toFriendlyMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const deleteCampaign = async () => {
    if (!deleteTarget?.id) return;
    setBusyId(deleteTarget.id);
    setErrorMsg("");
    try {
      await fetchData(`/campaigns/broadcasts/${deleteTarget.id}`, "DELETE");
      setDeleteTarget(null);
      await refresh();
    } catch (err) {
      setErrorMsg(toFriendlyMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Disparo",
        flex: 1.4,
        minWidth: 200,
        renderCell: (params) => (
          <Box sx={{ minWidth: 0, width: "100%", py: 0.25 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, lineHeight: 1.35, overflow: "hidden", textOverflow: "ellipsis" }}
              noWrap
            >
              {params.value}
            </Typography>
            {params.row.tag ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.35, mt: 0.25 }}
                noWrap
              >
                Etiqueta: {params.row.tag}
              </Typography>
            ) : params.row.schedule_at ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.35, mt: 0.25 }}
                noWrap
              >
                Agenda: {formatScheduleAt(params.row.schedule_at)}
              </Typography>
            ) : null}
            {params.row.budget > 0 ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.35, mt: 0.25 }}
                noWrap
              >
                Orçamento: {BRL(params.row.budget)}
              </Typography>
            ) : null}
          </Box>
        ),
      },
      {
        field: "status",
        headerName: "Situação",
        width: 130,
        renderCell: (params) => (
          <Chip
            size="small"
            label={labelStatus(params.value)}
            color={statusColor(params.value)}
            variant={params.value === "active" || params.value === "running" ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "channels",
        headerName: "Canal",
        width: 220,
        sortable: false,
        renderCell: (params) => {
          const label = formatOutboundChannelLabel({
            channels: params.row.channels,
            originName: params.row.originName,
            originIdentity: params.row.originIdentity,
          });
          if (!label || label === "—") {
            return (
              <Typography variant="body2" color="text.disabled">
                —
              </Typography>
            );
          }
          return (
            <Tooltip title={label}>
              <Chip size="small" label={label} variant="outlined" sx={{ maxWidth: "100%" }} />
            </Tooltip>
          );
        },
      },
      {
        field: "pending",
        headerName: "Na fila",
        width: 100,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => formatCount(params.value) || "0",
      },
      {
        field: "sent",
        headerName: "Enviados",
        width: 100,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => formatCount(params.value) || "0",
      },
      {
        field: "failed",
        headerName: "Falhas",
        width: 90,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => formatCount(params.value) || "0",
      },
      {
        field: "actions",
        headerName: "Ações",
        width: 168,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const status = params.row.status;
          const canLaunch = ["draft", "scheduled", "paused"].includes(status);
          const busy = busyId === params.row.id;
          return (
            <Stack direction="row" spacing={0.25} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Abrir detalhes">
                <IconButton
                  size="small"
                  onClick={() => navigate(`/campaigns/whatsapp/${params.row.id}`)}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canUpdate ? (
                <Tooltip title="Editar orçamento">
                  <IconButton
                    size="small"
                    onClick={() => setBudgetEditTarget(params.row.raw || params.row)}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {canSend && canLaunch ? (
                <Tooltip
                  title={
                    status === "scheduled"
                      ? "Iniciar o envio agendado"
                      : status === "paused"
                        ? "Retomar o envio"
                        : "Enviar agora"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={busy || loading}
                      onClick={() => launch(params.row.id)}
                    >
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              {canExport ? (
                <Tooltip title="Baixar lista de destinatários (CSV)">
                  <span>
                    <IconButton
                      size="small"
                      disabled={busy || loading}
                      onClick={() => exportCsv(params.row.id)}
                    >
                      <FileDownloadOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              {canDelete ? (
                <Tooltip
                  title={
                    ["running", "scheduled", "paused"].includes(status)
                      ? "Cancele o envio antes de excluir"
                      : "Excluir envio"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={
                        busy ||
                        loading ||
                        ["running", "scheduled", "paused"].includes(status)
                      }
                      onClick={() =>
                        setDeleteTarget({
                          id: params.row.id,
                          name: params.row.name,
                          status,
                        })
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </Stack>
          );
        },
      },
    ],
    [busyId, canDelete, canExport, canSend, canUpdate, loading, navigate]
  );

  const headerActions = (
    <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
      <Tooltip title="Atualizar lista">
        <span>
          <IconButton onClick={refresh} disabled={loading} size="small">
            <RefreshIcon />
          </IconButton>
        </span>
      </Tooltip>
      {canCreate ? (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          disabled={loading}
          fullWidth={isMobile && !embedded}
        >
          Novo envio
        </Button>
      ) : null}
    </Stack>
  );

  return (
    <Box sx={embedded ? undefined : { p: { xs: 2, md: 3 }, pb: { xs: 4, md: 5 }, maxWidth: 1400, mx: "auto" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        spacing={2}
        mb={2.5}
      >
        <Box sx={{ minWidth: 0, maxWidth: "40rem" }}>
          {!embedded ? (
            <>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Envio em massa
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>
                Envie a mesma mensagem para vários contatos de uma vez pelo WhatsApp, e-mail ou SMS.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Envio em massa
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                Escolha o canal, escreva a mensagem, defina quem recebe e envie na hora ou agende.
              </Typography>
            </>
          )}
          {!loading && campaigns.length ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              {campaigns.length} envio{campaigns.length === 1 ? "" : "s"}
              {runningCount ? ` · ${runningCount} em andamento/agendado` : ""}
              {draftCount ? ` · ${draftCount} rascunho${draftCount === 1 ? "" : "s"}` : ""}
            </Typography>
          ) : null}
        </Box>
        {headerActions}
      </Stack>

      {errorMsg ? (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      ) : null}

      {loading && !campaigns.length ? (
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2 }} />
      ) : !loading && !campaigns.length ? (
        <Paper
          variant="outlined"
          sx={{
            px: { xs: 2.5, sm: 4 },
            py: { xs: 3, sm: 4 },
            textAlign: "center",
            borderStyle: "dashed",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
            Nenhum envio em massa ainda
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2.5, maxWidth: 440, mx: "auto", lineHeight: 1.55 }}
          >
            Escolha o canal, escreva a mensagem, defina quem recebe e envie na hora ou deixe
            agendado.
          </Typography>
          {canCreate ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              Novo envio
            </Button>
          ) : null}
        </Paper>
      ) : (
        <Paper variant="outlined">
          <GenericTable
            rows={rows}
            columns={columns}
            rowCount={rows.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            onSearch={(term) => {
              setSearch(term);
              setPage(0);
            }}
            isLoading={loading}
            paginationMode="client"
            exportFileName="campanhas-disparos"
            onExportPDFTitle="Disparos selecionados"
            onRowClick={(row) => navigate(`/campaigns/whatsapp/${row.id}`)}
            getRowHeight={() => "auto"}
          />
        </Paper>
      )}

      {canCreate ? (
        <OmnichannelCampaignCreateDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => {
            setCreateOpen(false);
            refresh();
            if (id) navigate(`/campaigns/whatsapp/${id}`);
          }}
        />
      ) : null}

      <BroadcastBudgetEditDialog
        open={Boolean(budgetEditTarget)}
        campaign={budgetEditTarget}
        onClose={() => setBudgetEditTarget(null)}
        onSaved={refresh}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => (busyId ? null : setDeleteTarget(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Excluir envio?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            O envio <strong>{deleteTarget?.name}</strong> e seu histórico de destinatários,
            métricas e eventos serão excluídos permanentemente. As conversas e vendas existentes
            serão mantidas, mas deixarão de estar vinculadas a este envio.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={Boolean(busyId)}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={deleteCampaign}
            disabled={Boolean(busyId)}
          >
            {busyId ? "Excluindo…" : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
